import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Phone, ArrowRight } from 'lucide-react';
import { setLeadSource } from '@/lib/leadSource';

/**
 * Sticky mobile bottom CTA — "Prenota una call".
 * Visible only on mobile (<md). Sits ABOVE the chatbot floating button
 * (chatbot uses z-[10000] at bottom-2). Auto-hides when the user is
 * already inside the #book-call section.
 */
export const StickyMobileCTA = () => {
  const { locale } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  // Hide while the cookie banner is on screen — initial state computed
  // from localStorage to avoid a flash on first paint.
  const [cookieBannerOpen, setCookieBannerOpen] = useState(() => {
    try {
      return !localStorage.getItem('pv_cookie_consent');
    } catch {
      return false;
    }
  });

  const label = locale === 'en' ? 'Book a call' : 'Prenota una call';
  const helper = locale === 'en' ? 'Free · 30 min · we call you' : 'Gratis · 30 min · ti richiamiamo';

  useEffect(() => {
    setMounted(true);
    // Show only after the user has scrolled a bit so it doesn't compete with the hero CTA
    const onScroll = () => {
      const y = window.scrollY || 0;
      const bookCall = document.getElementById('book-call');
      let insideBookCall = false;
      if (bookCall) {
        const rect = bookCall.getBoundingClientRect();
        // hide if section is in viewport
        insideBookCall = rect.top < window.innerHeight * 0.7 && rect.bottom > window.innerHeight * 0.2;
      }
      setVisible(y > 480 && !insideBookCall);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    const onBannerVisible = (e) => setCookieBannerOpen(!!e.detail?.visible);
    window.addEventListener('pv:cookie-banner-visible', onBannerVisible);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('pv:cookie-banner-visible', onBannerVisible);
    };
  }, []);

  if (!mounted) return null;
  // While cookie banner is on screen we never show the sticky CTA, regardless of scroll.
  const reallyVisible = visible && !cookieBannerOpen;

  return (
    <div
      data-testid="pv-sticky-mobile-cta"
      className={`md:hidden fixed left-0 right-0 z-[9500] px-3 transition-all duration-300 ease-out ${
        reallyVisible ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
      style={{ bottom: 'calc(76px + env(safe-area-inset-bottom, 0px))' }}
    >
      <a
        href="/bookacall"
        data-testid="pv-sticky-mobile-cta-link"
        onClick={() => setLeadSource('sticky-mobile')}
        className="group flex items-center justify-between gap-3 w-full pl-4 pr-3 py-3 rounded-2xl text-white shadow-[0_18px_40px_-10px_rgba(255,107,53,0.55)] border border-white/20 backdrop-blur-md active:scale-[0.98] transition-transform"
        style={{ background: 'linear-gradient(135deg, #FF7A47 0%, #FF6B35 55%, #F25A1F 100%)' }}
      >
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/20 shrink-0">
          <Phone className="w-4.5 h-4.5 text-white" strokeWidth={2.3} />
        </span>
        <span className="flex-1 flex flex-col items-start leading-none min-w-0">
          <span className="text-[14.5px] font-bold tracking-normal">{label}</span>
          <span className="text-[10.5px] font-mono uppercase tracking-[0.16em] text-white/90 mt-1 font-bold truncate w-full">
            {helper}
          </span>
        </span>
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white/15 shrink-0 transition-transform group-active:translate-x-0.5">
          <ArrowRight className="w-4 h-4 text-white" strokeWidth={2.5} />
        </span>
      </a>
    </div>
  );
};

export default StickyMobileCTA;
